import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { IMerchantSimple, MerchantStatusType } from "../../interfaces";
import { api } from "../../api";
import { useApi } from "../../hooks";

interface MerchantStatusModalProps {
  show: boolean;
  onHide: () => void;
  merchant: IMerchantSimple | null;
  onSuccess: () => void;
}

export const MerchantStatusModal: React.FC<MerchantStatusModalProps> = ({
  show,
  onHide,
  merchant,
  onSuccess,
}) => {
  const { post } = useApi();
  const [statusType, setStatusType] = useState<MerchantStatusType | "">("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form when modal opens with merchant data
  React.useEffect(() => {
    if (merchant && show) {
      setStatusType(merchant.statusType || "");
      setStatusMessage(merchant.statusMessage || "");
      setError(null);
    }
  }, [merchant, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!merchant || !statusType) return;

    try {
      setIsSubmitting(true);
      setError(null);

      await post(`/merchants/${merchant._id}/status`, {
        statusType,
        statusMessage,
      });

      setIsSubmitting(false);
      onSuccess();
      onHide();
    } catch (err: any) {
      setError(err.message || "Failed to update status");
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Update Merchant Status</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {merchant && (
            <>
              <p>
                <strong>Merchant:</strong> {merchant.name}
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Status Type</Form.Label>
                <Form.Select
                  value={statusType}
                  onChange={(e) =>
                    setStatusType(e.target.value as MerchantStatusType)
                  }
                  required
                >
                  <option value="">Select a status...</option>
                  {Object.values(MerchantStatusType).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={statusMessage}
                  onChange={(e) => setStatusMessage(e.target.value)}
                  placeholder="Add details about the status..."
                />
              </Form.Group>

              {error && (
                <div className="alert alert-danger mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update Status"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
